import { ref, reactive } from 'vue';
import axios from 'axios';

export function useOracleHcrCertificate() {
  const self = {
    certStatus: reactive({
      active: null,
      pending: null,
      history: [],
      environment: null
    }),
    generating: ref(false),
    activating: ref(false),
    rollingBack: ref(false),
    form: reactive({
      validityDays: 365
    }),
  };

  function _triggerDownload(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  function _formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  }

  function _statusColor(status) {
    return { active: 'success', pending: 'orange', rotated: 'grey' }[status] || 'grey';
  }

  self.formatDate = (dateStr) => _formatDate(dateStr);
  self.statusColor = (status) => _statusColor(status);

  self.fetchStatus = async () => {
    try {
      const resp = await axios.get('admin/oracleHcrCertificateStatus');
      if (resp.data.Status === 'OK') {
        Object.assign(self.certStatus, resp.data.Data);
      }
    } catch (err) {
      console.error('Failed to fetch cert status', err);
    }
  };

  self.generateCertificate = async (notify) => {
    self.generating.value = true;
    try {
      const resp = await axios.post('admin/generateOracleHcrCertificate', {
        validityDays: self.form.validityDays
      }, { responseType: 'blob' });

      _triggerDownload(new Blob([resp.data]), 'oracle-hcr-certificate.zip');

      notify?.('Certificate generated and downloaded. Send the public cert to the vendor.', 'success');
      await self.fetchStatus();
    } catch (err) {
      notify?.('Failed to generate certificate', 'error');
      console.error(err);
    } finally {
      self.generating.value = false;
    }
  };

  self.activateCertificate = async (notify) => {
    self.activating.value = true;
    try {
      const resp = await axios.post('admin/activateOracleHcrCertificate');
      if (resp.data.Status === 'OK') {
        notify?.('Certificate activated successfully', 'success');
        await self.fetchStatus();
      } else {
        notify?.(resp.data.Message || 'Activation failed', 'error');
      }
    } catch (err) {
      notify?.('Failed to activate certificate', 'error');
      console.error(err);
    } finally {
      self.activating.value = false;
    }
  };

  self.rollbackCertificate = async (ohch_id, notify) => {
    self.rollingBack.value = true;
    try {
      const resp = await axios.post('admin/rollbackOracleHcrCertificate', { ohch_id });
      if (resp.data.Status === 'OK') {
        notify?.('Certificate rolled back successfully', 'success');
        await self.fetchStatus();
      } else {
        notify?.(resp.data.Message || 'Rollback failed', 'error');
      }
    } catch (err) {
      notify?.('Failed to rollback certificate', 'error');
      console.error(err);
    } finally {
      self.rollingBack.value = false;
    }
  };

  self.downloadCert = async (ohch_id, notify) => {
    try {
      const resp = await axios.get(`admin/downloadOracleHcrCertificate/${ohch_id}`, { responseType: 'blob' });
      _triggerDownload(new Blob([resp.data]), 'oracle-hcr-certificate.zip');
    } catch (err) {
      notify?.('Failed to download certificate', 'error');
      console.error(err);
    }
  };

  return self;
}
